import debounce from 'lodash/debounce';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useContextSelector } from 'use-context-selector';

import { getRecentVisits } from '@/core/apis/asset-manager-apis';
import { cacheSetAction } from '@/core/store/cache/cache-reducers';
import { CacheContext, setCacheState } from '@/core/store/cache/CacheProvider';
import { usePagination } from '@/core/utils/usePagination';
import { appSettings } from '@/settings/app-settings';
import { StoreFront } from '@/ux/pages/StoreFrontPage/StoreFront';
import Loader from '@/ux-legacy/components/common/Loader/Loader';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';

type StoresListingProps = {
  userId?: string;
};

const StoresListing: FC<StoresListingProps> = ({ userId }) => {
  const [stores, setStores] = useState<{ storefront: StoreFront; visitCount: number; _id: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const router = useRouter();
  const cacheDispatch = useContextSelector(CacheContext, setCacheState);
  const LIMIT = 10;

  const fetchStoreFronts = (limit: number, offset: number, existingStores: any[], search?: string) => {
    getRecentVisits({ limit, offset, type: 'STOREFRONT', search }).then((res) => {
      setIsLoading(false);
      setStores([...existingStores, ...res.visits]);
      setCurrentCount([...existingStores, ...res.visits].length);
      setHasMore(res.visits.length == limit);
    });
  };

  const loadMore = () => {
    fetchStoreFronts(10, 0, stores, searchQuery);
    setCurrentOffset(LIMIT + offset);
    setIsLoading(true);
  };

  const { count, offset, moreElement, setCurrentCount, setCurrentOffset } = usePagination(loadMore, 0, [
    searchQuery,
  ]);

  const onSearch = (search: string) => {
    fetchStoreFronts(LIMIT, 0, [], search);
    setIsLoading(true);
    setHasMore(true);
    setCurrentOffset(0);
    setStores([]);
  };

  const debouncedSearch = useCallback(debounce(onSearch, 500), []);

  useEffect(() => {
    fetchStoreFronts(LIMIT, 0, []);
  }, []);

  return (
    <div className="w-full h-full flex flex-col gap-y-1 overflow-y-auto">
      <div className="flex w-full relative">
        <FaSearch className="h-4 w-4 text-slate-400 absolute top-1/2 -translate-y-1/2 translate-x-2" />
        <input
          placeholder="Search Portals"
          className="w-full pr-4 pl-8 py-2 outline-none border-none bg-gray-200 rounded-full"
          value={searchQuery}
          autoComplete="off"
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedSearch(e.target.value);
          }}
        ></input>
      </div>

      {stores.map((store, index) => {
        return (
          <button
            key={store.storefront._id}
            onClick={() => {
              router.push(`/store/${store.storefront.slug}`);
              if (userId) {
                cacheDispatch(
                  cacheSetAction({
                    [`SetProfilePageOpen_${userId}`]: undefined,
                  }),
                );
              }
            }}
            className="w-full flex items-center p-2 gap-x-2 hover:bg-slate-50 rounded-md text-left"
          >
            <img
              src={
                store.storefront.image
                  ? appSettings.api.S3_MEDIA_COMPRESSED + store.storefront.image
                  : IMG_PLACEHOLDER
              }
              alt=""
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            ></img>
            <span className="w-0 flex-grow truncate font-bold">{store.storefront.name}</span>
          </button>
        );
      })}
      {!isLoading && hasMore && <div ref={moreElement} className="w-full h-[50px] flex"></div>}
      {isLoading && (
        <div className="w-full h-[100px] flex items-center justify-center">
          <Loader />
        </div>
      )}
      {!isLoading && stores.length == 0 && (
        <div className="w-full h-[100px] flex items-center justify-center italic">No Stores Found.</div>
      )}
    </div>
  );
};

export default StoresListing;
